import { useForm, SubmitHandler } from "react-hook-form";
import { useState } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocation, useNavigate } from "react-router-dom";
import { FormField } from "@/components/FormField";
import { FormRoot } from "@/components/FormRoot";
import { FormSubmit } from "@/components/FormSubmit";
import { SelectRoot } from "@/components/Select";
import { useToast } from "@/hooks/useToast";

const contactSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters").describe("Full Name"),
  email: z.string().email("Please enter a valid email address").describe("Email Address"),
  subject: z.string().min(1, "Please select a subject").describe("Subject"),
  message: z.string().min(10, "Message must be at least 10 characters").describe("Message"),
});

type ContactFormInputs = z.infer<typeof contactSchema>;

const subjectOptions = [
  { label: "General Inquiry", value: "general" },
  { label: "Technical Support", value: "technical" },
  { label: "Account Issues", value: "account" },
  { label: "Partnership/Business", value: "business" },
  { label: "Feedback", value: "feedback" },
  { label: "Report a Problem", value: "problem" },
  { label: "Other", value: "other" },
];

export const ContactUs = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { showToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const backTo = location.state?.from || "/";

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm<ContactFormInputs>({
    resolver: zodResolver(contactSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      name: "",
      email: "",
      subject: "",
      message: "",
    },
  });

  const onSubmit: SubmitHandler<ContactFormInputs> = async (data) => {
    setLoading(true);
    try {
      // Simulate API call - replace with actual API endpoint
      await new Promise((resolve) => setTimeout(resolve, 1000));

      console.log("Contact form submission:", data);
      showToast({
        type: "success",
        message: "Thank you for your message! We'll get back to you within 24 hours.",
      });
      setSubmitted(true);
      reset();
    } catch (error) {
      console.error("Error submitting contact form:", error);
      showToast({
        type: "error",
        message: "Sorry, there was an error sending your message. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto p-6 text-center">
        <div className="mb-6">
          <button
            type="button"
            onClick={() => navigate(backTo)}
            className="text-primary-600 text-sm"
          >
            ← Back
          </button>
        </div>

        <div className="bg-theme-bg-success border border-theme-border-success rounded-lg p-8">
          <div className="text-theme-text-success mb-4">
            <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-theme-text-success mb-2">Message Sent!</h2>
          <p className="text-theme-text-success mb-4">
            Thank you for contacting us. We've received your message and will get back to you within
            24 hours.
          </p>
          <button
            onClick={() => setSubmitted(false)}
            className="text-primary-600 hover:text-primary-800 font-medium"
          >
            Send Another Message
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="mb-6">
        <button type="button" onClick={() => navigate(backTo)} className="text-primary-600 text-sm">
          ← Back
        </button>
      </div>

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-theme-text-heading mb-4">Contact Us</h1>
        <p className="text-theme-text-secondary">
          Have questions or need help? We'd love to hear from you. Send us a message and we'll
          respond as soon as possible.
        </p>
      </div>

      <FormRoot size="large" onSubmitHandler={handleSubmit(onSubmit)}>
        <div className="space-y-6">
          <FormField name="name" required label="Full Name" error={errors.name?.message}>
            <input
              {...register("name")}
              type="text"
              className="w-full px-3 py-2 border border-theme-border rounded-md shadow-sm bg-theme-bg-surface text-theme-text focus:outline-none focus:ring-theme-accent focus:border-theme-accent"
              placeholder="Enter your full name"
            />
          </FormField>

          <FormField name="email" required label="Email Address" error={errors.email?.message}>
            <input
              {...register("email")}
              type="email"
              className="w-full px-3 py-2 border border-theme-border rounded-md shadow-sm bg-theme-bg-surface text-theme-text focus:outline-none focus:ring-theme-accent focus:border-theme-accent"
              placeholder="Enter your email address"
            />
          </FormField>

          <FormField name="subject" required label="Subject" error={errors.subject?.message}>
            <SelectRoot
              name="subject"
              onValueChange={(value) =>
                setValue("subject", value, { shouldValidate: true, shouldTouch: true })
              }
              options={subjectOptions}
              placeholder="Select a subject"
            />
          </FormField>

          <FormField name="message" required label="Message" error={errors.message?.message}>
            <textarea
              {...register("message")}
              rows={6}
              className="w-full px-3 py-2 border border-theme-border rounded-md shadow-sm bg-theme-bg-surface text-theme-text focus:outline-none focus:ring-theme-accent focus:border-theme-accent resize-vertical"
              placeholder="Please describe your inquiry or feedback in detail..."
            />
          </FormField>

          <FormSubmit className="bg-primary-gradient" isLoading={loading} disabled={loading}>
            {loading ? "Sending..." : "Send Message"}
          </FormSubmit>
        </div>
      </FormRoot>

      <div className="mt-8 pt-8 border-t border-theme-border">
        <h3 className="text-lg font-semibold text-theme-text-heading mb-4">
          Other Ways to Reach Us
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-theme-text-heading mb-2">Email</h4>
            <p className="text-theme-text-secondary">hello@forwardachieve.co</p>
          </div>
          <div>
            <h4 className="font-medium text-theme-text-heading mb-2">Response Time</h4>
            <p className="text-theme-text-secondary">We typically respond within 24 hours</p>
          </div>
        </div>
      </div>
    </div>
  );
};
